import { useState } from "react";
import { useTranslation } from "react-i18next";

const EMAIL = "contacto@301mgmt.com";

const grainStyle: React.CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E\")",
  backgroundSize: "200px 200px",
};

const socialLinks = [
  { icon: "ri-instagram-line", label: "Instagram", handle: "@301mgmt", href: "https://www.instagram.com/301mgmt" },
  { icon: "ri-linkedin-line", label: "LinkedIn", handle: "301 Management", href: "https://www.linkedin.com/company/301mgmt" },
];

export default function ContactoSection() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      id="contacto"
      className="relative min-h-screen flex flex-col justify-center py-20 md:py-28 px-6 md:px-10 overflow-hidden"
      style={{ backgroundColor: "#0d0b1e" }}
    >
      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={grainStyle}></div>

      {/* Glow blobs */}
      <div
        className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(208,248,85,0.18) 0%, transparent 70%)" }}
      ></div>
      <div
        className="absolute -bottom-32 -right-20 w-[480px] h-[480px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(237,5,120,0.22) 0%, transparent 70%)" }}
      ></div>

      <div className="relative z-10 max-w-6xl mx-auto w-full">
        {/* Tag */}
        <div className="flex justify-center mb-8">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] -rotate-2"
            style={{ border: "1px solid rgba(208,248,85,0.4)", color: "#d0f855" }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#d0f855" }}></span>
            {t("contacto.tag")}
          </span>
        </div>

        {/* Headline */}
        <div className="text-center mb-12">
          <h2
            className="font-black leading-[0.85] text-white"
            style={{ letterSpacing: "-0.04em", fontSize: "clamp(2.75rem, 9vw, 7rem)" }}
          >
            {t("contacto.title1")}
            <br />
            <span className="font-thin italic" style={{ color: "rgba(255,255,255,0.35)" }}>
              {t("contacto.title2")}
            </span>
          </h2>
        </div>

        {/* Email CTA */}
        <div className="flex flex-col items-center gap-4 mb-16">
          <a
            href={`mailto:${EMAIL}`}
            className="group inline-flex items-center gap-2.5 md:gap-3 px-5 md:px-7 py-3 md:py-3.5 rounded-full font-black text-sm md:text-lg transition-transform hover:-rotate-1 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#d0f855", color: "#0d0b1e" }}
          >
            <i className="ri-mail-send-line text-base md:text-lg"></i>
            {EMAIL}
            <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full transition-transform group-hover:translate-x-1" style={{ backgroundColor: "#0d0b1e" }}>
              <i className="ri-arrow-right-up-line text-xs md:text-sm" style={{ color: "#d0f855" }}></i>
            </span>
          </a>

          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors cursor-pointer whitespace-nowrap"
            style={{ color: copied ? "#d0f855" : "rgba(255,255,255,0.4)" }}
          >
            {copied ? (
              <>
                <i className="ri-check-line"></i>
                {t("contacto.copied")}
              </>
            ) : (
              <>
                <i className="ri-file-copy-line"></i>
                {t("contacto.copy")}
              </>
            )}
          </button>
        </div>

        {/* Socials */}
        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4">
          {socialLinks.map((s, i) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 px-6 py-4 rounded-2xl transition-all hover:scale-[1.03] cursor-pointer"
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                transform: `rotate(${i === 0 ? "-1deg" : "1deg"})`,
              }}
            >
              <span
                className="w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0"
                style={{ backgroundColor: "rgba(237,5,120,0.15)" }}
              >
                <i className={`${s.icon} text-xl`} style={{ color: "#ed0578" }}></i>
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-white font-bold text-sm">{s.label}</span>
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
                  {s.handle}
                </span>
              </span>
              <span className="w-6 h-6 flex items-center justify-center transition-transform group-hover:translate-x-1">
                <i className="ri-arrow-right-line text-sm" style={{ color: "rgba(255,255,255,0.5)" }}></i>
              </span>
            </a>
          ))}
        </div>
      </div>

    </section>
  );
}